import React, { useRef } from 'react';
import { Optional } from '../types';
import { composeEvent, composeRef } from '../utils/props';

export interface UseRoverOptions {
  index: number;
  onIndexChange: (index: number, is_selecting: boolean) => void;
  length: number;
}

export interface UseRoverItemProps {
  onKeyDown: (e: React.KeyboardEvent) => void;
  onFocus: (e: React.FocusEvent) => void;
  tabIndex: number;
  ref: React.RefCallback<HTMLElement>;
}

export interface UseRoverItemCustomProps extends Optional<Omit<UseRoverItemProps, 'ref'>> {
  ref?: React.Ref<HTMLElement>;
}

export interface UseRover {
  getRoverItemProps(index: number, props: UseRoverItemCustomProps): UseRoverItemProps;
}

export function useRover(options: UseRoverOptions): UseRover {
  const { index: currentIndex, onIndexChange, length } = options;
  const refs = useRef<HTMLElement[]>([]);
  const setFocus = (index: number) => {
    const targetElem = refs.current[index];
    if (targetElem != null) {
      targetElem.focus();
    }
  };
  const move = (offset: number) => {
    const next_index = Math.floor((currentIndex + offset + length) % length);
    onIndexChange(next_index, true);
    setFocus(next_index);
  };
  return {
    getRoverItemProps: (index, props) => {
      return {
        tabIndex: index === currentIndex ? 0 : -1,
        ref: composeRef(props.ref, (elem) => {
          // @ts-expect-error: TS2522
          refs.current[index] = elem;
        }),
        onKeyDown: composeEvent(props.onKeyDown, (e) => {
          if (e.defaultPrevented) return;
          switch (e.key) {
            case 'ArrowLeft':
            case 'ArrowUp':
              move(-1);
              e.preventDefault();
              break;
            case 'ArrowRight':
            case 'ArrowDown':
              move(1);
              e.preventDefault();
              break;
            case 'Home':
              onIndexChange(0, true);
              setFocus(0);
              e.preventDefault();
              break;
            case 'End':
              onIndexChange(length - 1, true);
              setFocus(length - 1);
              e.preventDefault();
              break;
          }
        }),
        onFocus: composeEvent(props.onFocus, (e) => {
          if (e.defaultPrevented) return;
          if (index !== currentIndex) {
            onIndexChange(index, false);
          }
        }),
      };
    },
  };
}
